import * as angular from 'angular';
import * as _ from 'lodash';

import { Application } from '../../models/entities';
import { KeyValuePair } from '../../models/KeyValuePair';
import { ApplicationDto, ApplicationUserDTO, ApplicationWithStatus, AppOverviewDto, IApplicationInformation } from '../../models/DTO';
import { IRequestService } from '../request.service';
import { INgFileUploadService } from '../../services';

export interface ExcelUploadResults {
    [objectType: string]: { added: number; updated: number; ignored: number };
}

export interface IApplicationService {
    getAll(callback: Function, force?: boolean): void;
    getByGuid(guid: string, withDocumentation?: boolean, force?: boolean): angular.IPromise<ApplicationDto>;
    getByName(name: string): angular.IPromise<any>;
    getForUser(userName: string, callback: (data: ApplicationDto[], status: number) => void): void;
    getForUserAsync(userName: string, loadAll?: boolean): angular.IPromise<ApplicationDto[]>;
    getWithInformationForUser(userName: string, canceller?: angular.IPromise<void>): angular.IPromise<{ item1: Application; item2: IApplicationInformation }[]>;
    getOneWithInformation(
        userName: string,
        appGuid: string,
        canceller?: angular.IPromise<void>,
        loadLogs?: boolean
    ): angular.IPromise<{ item1: Application; item2: IApplicationInformation }>;
    getOverviews(): angular.IPromise<{ [guid: string]: AppOverviewDto }>;
    getAllWithStatus(force?: boolean): angular.IPromise<ApplicationWithStatus[]>;
    getUsers(guid: string): angular.IPromise<ApplicationUserDTO[]>;
    getRolesForApplication(guid: string, username: string): angular.IPromise<string[]>;
    getRolesForUser(username: string): angular.IPromise<KeyValuePair<string, string[]>[]>;
    getExport(guid: string, callback: Function): void;
    getApplicationDocumentation(guid: string): angular.IPromise<string>;
    updateAll(applicationGuids: string[]): angular.IPromise<any>;
    isDbUpToDate(applicationGuids: string[], username: string): angular.IPromise<{ key: string; value: boolean }[]>;
    updateUsers(guid: string, users: any[]): angular.IPromise<ApplicationUserDTO[]>;
    postImport(importConfig: any): angular.IPromise<any>;
    updateFromFile(importConfig: any): angular.IPromise<any>;
    upload(files: any, progressCalback?): angular.IPromise<any>;
    update(application): angular.IPromise<any>;
    delete(application): angular.IPromise<any>;
    add(application): angular.IPromise<any>;
    cancelAllTasks(applicationGuid?: string): angular.IPromise<void>;
    resumeAllTasks(applicationGuid?: string): angular.IPromise<void>;
    reloadCache(): angular.IPromise<void>;
    setIdle(appGuid: string, isIdle: boolean);
    downloadExcel(): angular.IPromise<void>;
    uploadExcel(file: File): angular.IPromise<ExcelUploadResults>;
}

export class ApplicationService implements IApplicationService {
    static serviceId = 'applicationService';

    applications: Application[];
    private _controllerUrl: string;

    static $inject = ['$q', 'requestService', 'Upload'];

    constructor(private _$q: angular.IQService, private _requestService: IRequestService, private _upload: INgFileUploadService) {
        this.applications = [];
    }

    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + 'application/';
        }
        return this._controllerUrl;
    }

    getAll(successCallback: Function, force: boolean = false): void {
        const url = this.controllerUrl();
        if (force) {
            this._requestService.cleanCache(url);
        }
        this._requestService.get(url, undefined, true).then(
            (data) => {
                successCallback(data, 0);
            },
            (reason) => {
                console.error(reason);
            }
        );
    }

    getByGuid(guid: string, withDocumentation?: boolean): angular.IPromise<any> {
        const url = `${this.controllerUrl()}${guid}/${!!withDocumentation}`;
        return this._requestService.get(url, undefined, true);
    }

    getForUser(userName: string, successCallback: Function): void {
        const url = this.controllerUrl() + 'user/' + userName + '/true/';
        // Clean cache before request, as add/delete cannot clean it properly due to the fact that user name is required
        this._requestService.cleanCache(url);
        this._requestService
            .get(url, undefined, true)
            .then((data) => {
                successCallback(data, 0);
            })
            .catch((reason) => {
                console.error(reason);
            });
    }

    getForUserAsync(userName: string, loadAll?: boolean): angular.IPromise<ApplicationDto[]> {
        const url = this.controllerUrl() + 'user/' + userName + '/' + (loadAll !== false) + '/';
        // Clean cache before request, as add/delete cannot clean it properly due to the fact that user name is required
        this._requestService.cleanCache(url);
        return this._requestService.get(url, undefined, true);
    }

    getWithInformationForUser(
        userName: string,
        canceller?: angular.IPromise<void>
    ): angular.IPromise<{ item1: Application; item2: IApplicationInformation }[]> {
        const url = 'application/information/user/' + userName + '/';
        this._requestService.cleanCacheAtV1(url);
        return this._requestService.getAtV1(url, undefined, false, canceller);
    }

    getOneWithInformation(
        userName: string,
        appGuid: string,
        canceller?: angular.IPromise<void>,
        loadLogs?: boolean
    ): angular.IPromise<{ item1: Application; item2: IApplicationInformation }> {
        const url = 'application/information/user/' + userName + '/' + appGuid + '/' + (loadLogs ?? 'false');
        this._requestService.cleanCacheAtV1(url);
        return this._requestService.getAtV1(url, undefined, false, canceller);
    }

    getExport(guid: string, successCallback: Function): void {
        const url = this.controllerUrl() + 'export/' + guid + '/';
        this._requestService.get(url).then((data) => {
            successCallback(data, this._requestService.getApiUrl(), 0);
        });
    }

    getApplicationDocumentation(guid: string): angular.IPromise<string> {
        const url = `${this.controllerUrl()}documentation/${guid}`;
        this._requestService.cleanCache(`${this.controllerUrl()}documentation/${guid}`);
        return this._requestService.get(url, undefined, true);
    }

    postImport(importConfig: any): angular.IPromise<any> {
        const url = this.controllerUrl() + 'fromFile/';
        this._requestService.cleanCache(this.controllerUrl());
        const promise = this._requestService.post(url, importConfig, undefined, true);
        return promise;
    }

    updateFromFile(importConfig: any): angular.IPromise<any> {
        const url = this.controllerUrl() + 'updateFromFile/';
        this._requestService.cleanCache(this.controllerUrl());
        const promise = this._requestService.post(url, importConfig, undefined, true);
        return promise;
    }

    upload(file: any, progressCalback?): angular.IPromise<any> {
        const url = this.controllerUrl() + 'upload/';
        const data = {
            url: url,
            file: file
        };
        return this._upload.upload(data).progress((evt) => {
            progressCalback(evt);
        });
    }

    add(application): angular.IPromise<any> {
        const url = this.controllerUrl() + 'create/';
        const promise = this._requestService.post(url, application, undefined, true);
        return promise;
    }

    update(application): angular.IPromise<any> {
        const url = this.controllerUrl();
        return this._requestService.post(url, application, undefined, true).then((data) => {
            this._requestService.cleanCache(this.controllerUrl());
            this._requestService.cleanCache(this.controllerUrl() + application.guid);
            this._requestService.cleanCache(`${this.controllerUrl()}${application.guid}/true`);
            return data;
        });
    }

    delete(application): angular.IPromise<any> {
        const url = this.controllerUrl();
        this._requestService.cleanCache(url);
        return this._requestService.delete(url, application);
    }

    getUsers(guid: string): angular.IPromise<ApplicationUserDTO[]> {
        const url = this.controllerUrl() + 'users/' + guid + '/';
        return this._requestService.get(url, undefined, true);
    }

    updateUsers(guid: string, users: any[]): angular.IPromise<ApplicationUserDTO[]> {
        const url = 'application/users/' + guid + '/';
        return this._requestService.postAtV1<ApplicationUserDTO[]>(url, users, undefined, true).then((data) => {
            this._requestService.cleanAllCache();
            return data;
        });
    }

    getRolesForApplication(guid: string, username: string): angular.IPromise<string[]> {
        const url = this.controllerUrl() + 'roles/' + guid + '/' + username + '/';
        return this._requestService.get(url, undefined, true);
    }

    getRolesForUser(username: string): angular.IPromise<KeyValuePair<string, string[]>[]> {
        const url = this.controllerUrl() + 'roles/user/' + username + '/';
        return this._requestService.get(url, undefined, true);
    }

    getByName(name: string): angular.IPromise<any> {
        const defered = this._$q.defer();
        this.getAll((data) => {
            const applications = _.map(data, 'application');
            const application = _.find(applications, (a) => a.name === name);
            if (application) {
                defered.resolve(application);
            } else {
                defered.reject({ message: 'application not found' });
            }
        });
        return defered.promise;
    }

    getAllWithStatus(force: boolean = false): angular.IPromise<any[]> {
        const applicationGetallwithstatus = this.controllerUrl() + 'getAllWithStatus/';
        if (force) {
            this._requestService.cleanCache(applicationGetallwithstatus);
        }
        return this._requestService.get(applicationGetallwithstatus);
    }

    isDbUpToDate(applicationGuids: string[], username: string): angular.IPromise<{ key: string; value: boolean }[]> {
        return this._requestService.post(this.controllerUrl() + 'isDbUpToDate/user/' + username + '/', applicationGuids);
    }

    updateAll(applicationGuids: string[]): angular.IPromise<any> {
        this._requestService.cleanCache(this.controllerUrl() + 'getAllWithStatus/');
        return this._requestService.post(this.controllerUrl() + 'updateAll/', applicationGuids);
    }

    reloadCache(): angular.IPromise<void> {
        const url = 'application/reloadCache/';
        return this._requestService.getAtV1<void>(url).then((data) => {
            this._requestService.cleanAllCache();
            return data;
        });
    }

    getOverviews(): angular.IPromise<{ [guid: string]: AppOverviewDto }> {
        const url = this.controllerUrl() + 'information/overviews/';
        this._requestService.cleanCache(url);
        return this._requestService.get(url);
    }

    cancelAllTasks(applicationGuid?: string): angular.IPromise<void> {
        const url = this.controllerUrl() + 'tasks/cancelAll' + (applicationGuid ? '/' + applicationGuid + '/' : '');
        return this._requestService.post(url);
    }

    resumeAllTasks(applicationGuid?: string): angular.IPromise<void> {
        const url = this.controllerUrl() + 'tasks/resumeAll' + (applicationGuid ? '/' + applicationGuid + '/' : '');
        return this._requestService.post(url);
    }

    setIdle(appGuid: string, isIdle: boolean): angular.IPromise<Application> {
        const url = this.controllerUrl() + 'idle/' + appGuid;
        return this._requestService.post(url, isIdle);
    }

    downloadExcel(): angular.IPromise<void> {
        const url = `${this.controllerUrl()}excel`;
        this._requestService.cleanCache(url);
        return this._requestService.get(url).then((data) => {
            window.open(this._requestService.getApiUrl() + data);
        });
    }

    uploadExcel(file: File): angular.IPromise<ExcelUploadResults> {
        const url = `${this.controllerUrl()}excel`;
        return this._upload.upload({ url, file }).then((data) => {
            this._requestService.cleanAllCache();
            return data.data;
        });
    }
}
